import React, { Fragment, useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
import { Box, Button, Card, Chip, Container, Divider, Grid, IconButton, InputAdornment, Skeleton, Stack, SwipeableDrawer, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import { toast } from 'react-toastify';
import { accessVerify } from '../../../utils/common';
import {
  getSopDepartments,
  addSopDepartment,
  updateSopDepartment,
  deleteSopDepartment,
  getSopCategories,
  addSopCategory,
  updateSopCategory,
  deleteSopCategory,
  getSopSubcategories,
  addSopSubcategory,
  updateSopSubcategory,
  deleteSopSubcategory,
  getSopDocuments,
  addSopDocument,
  updateSopDocument,
  deleteSopDocument,
  clearCategories,
  clearSubcategories,
  clearDocuments,
  clearMutationState,
} from '../../../redux/slices/sopRedux/sopRedux';
import { fileUpload } from '../../../inteceptor';
import Label from '../../../components/Label';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const LEVEL = { DEPARTMENT: 'department', CATEGORY: 'category', SUBCATEGORY: 'subcategory', DOCUMENT: 'document' };

const isDocumentPublished = (value) => value?.isPublished === true || value?.isPublished === 1 || value?.isPublished === '1';

const LEVEL_CONFIG = {
  [LEVEL.DEPARTMENT]: {
    label: 'Department',
    pluralLabel: 'Departments',
    icon: 'mdi:office-building',
    color: '#1565C0',
    bgColor: '#E3F2FD',
  },
  [LEVEL.CATEGORY]: {
    label: 'Category',
    pluralLabel: 'Categories',
    icon: 'mdi:folder-open',
    color: '#2E7D32',
    bgColor: '#E8F5E9',
  },
  [LEVEL.SUBCATEGORY]: {
    label: 'Subcategory',
    pluralLabel: 'Subcategories',
    icon: 'mdi:folder-multiple',
    color: '#E65100',
    bgColor: '#FFF3E0',
  },
  [LEVEL.DOCUMENT]: {
    label: 'Document',
    pluralLabel: 'Documents',
    icon: 'mdi:file-document',
    color: '#6A1B9A',
    bgColor: '#F3E5F5',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper: FolderCard
// ─────────────────────────────────────────────────────────────────────────────

function FolderCard({ item, level, onOpen, onEdit, isAdmin }) {
  const cfg = LEVEL_CONFIG[level];
  return (
    <Card
      sx={{
        p: 2.5,
        cursor: 'pointer',
        border: `1.5px solid transparent`,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: cfg.color,
          boxShadow: `0 4px 16px ${cfg.color}22`,
          transform: 'translateY(-2px)',
        },
      }}
      onClick={() => onOpen(item)}
    >
      <Stack spacing={2.5}>
        <Stack direction='row' alignItems='center' spacing={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: cfg.bgColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Iconify icon={cfg.icon} sx={{ width: 28, height: 28, color: cfg.color }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='subtitle2' noWrap>
              {item.name}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {LEVEL_CONFIG[level].label}
            </Typography>
          </Box>
          {isAdmin && (
            <Tooltip title='Edit'>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(item);
                }}
              >
                <Iconify icon='eva:edit-2-outline' sx={{ width: 18, height: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Box
          sx={{
            minHeight: 48,
            borderRadius: 2,
            bgcolor: alpha(cfg.color, 0.04),
            p: 1.5,
          }}
        >
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.description || `Open this ${cfg.label.toLowerCase()} to continue browsing the SOP library.`}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
}

function FolderGridSkeleton() {
  return (
    <Grid container spacing={2}>
      {Array.from({ length: 8 }).map((_, index) => (
        <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
          <Card sx={{ p: 2.5 }}>
            <Stack spacing={2.5}>
              <Stack direction='row' spacing={2} alignItems='center'>
                <Skeleton variant='rounded' width={48} height={48} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width='70%' height={24} />
                  <Skeleton width='40%' height={20} />
                </Box>
              </Stack>
              <Skeleton variant='rounded' width='100%' height={72} />
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export default function SopPage() {
  const { themeStretch } = useSettings();
  const isAdmin = accessVerify('SOP_ADMIN') || accessVerify('SUPER_ADMIN');

  // ── Redux state ──────────────────────────────────────────────────────────
  const { departments, categories, subcategories, documents, addData, isLoading } = useSelector((state) => state.sop);

  // ── Drill-down navigation state ──────────────────────────────────────────
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  // ── Drawer (add / edit) state ─────────────────────────────────────────────
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAdd, setIsAdd] = useState(true);
  const [drawerLevel, setDrawerLevel] = useState(LEVEL.DEPARTMENT);
  const emptyForm = { name: '', description: '', isPublished: false };
  const [formData, setFormData] = useState(emptyForm);
  const [uploadedFileUrl, setUploadedFileUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [documentVisibility, setDocumentVisibility] = useState('all');
  const [backHistory, setBackHistory] = useState([]);
  const [forwardHistory, setForwardHistory] = useState([]);

  // ── Row selection for DataGridTable ──────────────────────────────────────
  const [rowSelection, setRowSelection] = useState({});

  // ─────────────────────────────────────────────────────────────────────────
  // Data loading
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    dispatch(getSopDepartments());
  }, []);

  useEffect(() => {
    if (selectedDepartment) dispatch(getSopCategories(selectedDepartment.id));
  }, [selectedDepartment]);

  useEffect(() => {
    if (selectedCategory) dispatch(getSopSubcategories(selectedCategory.id));
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedSubcategory) dispatch(getSopDocuments(selectedSubcategory.id));
  }, [selectedSubcategory]);

  // Refresh on successful mutation
  useEffect(() => {
    if (addData?.data) {
      setIsDrawerOpen(false);
      setFormData(emptyForm);
      setUploadedFileUrl('');
      setRowSelection({});
      // Reload the current visible level
      if (!selectedDepartment) dispatch(getSopDepartments());
      else if (!selectedCategory) dispatch(getSopCategories(selectedDepartment.id));
      else if (!selectedSubcategory) dispatch(getSopSubcategories(selectedCategory.id));
      else dispatch(getSopDocuments(selectedSubcategory.id));
      dispatch(clearMutationState());
    }
  }, [addData]);

  const currentLevel = !selectedDepartment ? LEVEL.DEPARTMENT : !selectedCategory ? LEVEL.CATEGORY : !selectedSubcategory ? LEVEL.SUBCATEGORY : LEVEL.DOCUMENT;

  const currentLevelCfg = LEVEL_CONFIG[currentLevel];
  const currentPathName = selectedSubcategory?.name || selectedCategory?.name || selectedDepartment?.name || 'Standard Operating Procedures';
  const currentPath = {
    department: selectedDepartment,
    category: selectedCategory,
    subcategory: selectedSubcategory,
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Current list to display
  // ─────────────────────────────────────────────────────────────────────────

  const currentItems = currentLevel === LEVEL.DEPARTMENT ? departments : currentLevel === LEVEL.CATEGORY ? categories : currentLevel === LEVEL.SUBCATEGORY ? subcategories : documents;

  // Viewers only see published documents
  const visibleItems = currentLevel === LEVEL.DOCUMENT && !isAdmin ? (currentItems || []).filter((d) => isDocumentPublished(d)) : currentItems || [];
  const publishedDocumentCount = documents.filter((item) => isDocumentPublished(item)).length;
  const draftDocumentCount = documents.length - publishedDocumentCount;
  const normalizedSearchValue = searchValue.trim().toLowerCase();

  const filteredItems = visibleItems.filter((item) => {
    const matchesSearch = !normalizedSearchValue || [item?.name, item?.description].filter(Boolean).some((value) => value.toLowerCase().includes(normalizedSearchValue));

    if (!matchesSearch) return false;
    if (currentLevel !== LEVEL.DOCUMENT || documentVisibility === 'all') return true;
    return documentVisibility === 'published' ? isDocumentPublished(item) : !isDocumentPublished(item);
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Drawer helpers
  // ─────────────────────────────────────────────────────────────────────────

  const isSamePath = (first, second) =>
    (first?.department?.id || null) === (second?.department?.id || null) &&
    (first?.category?.id || null) === (second?.category?.id || null) &&
    (first?.subcategory?.id || null) === (second?.subcategory?.id || null);

  const applyNavigationState = (nextPath) => {
    const departmentChanged = (selectedDepartment?.id || null) !== (nextPath?.department?.id || null);
    const categoryChanged = (selectedCategory?.id || null) !== (nextPath?.category?.id || null);
    const subcategoryChanged = (selectedSubcategory?.id || null) !== (nextPath?.subcategory?.id || null);

    setSelectedDepartment(nextPath?.department || null);
    setSelectedCategory(nextPath?.category || null);
    setSelectedSubcategory(nextPath?.subcategory || null);
    setRowSelection({});
    setSearchValue('');
    setDocumentVisibility('all');

    if (!nextPath?.department) {
      dispatch(clearCategories());
      dispatch(clearSubcategories());
      dispatch(clearDocuments());
      return;
    }

    if (departmentChanged) {
      dispatch(clearCategories());
      dispatch(clearSubcategories());
      dispatch(clearDocuments());
      return;
    }

    if (!nextPath?.category || categoryChanged) {
      dispatch(clearSubcategories());
      dispatch(clearDocuments());
      return;
    }

    if (!nextPath?.subcategory || subcategoryChanged) {
      dispatch(clearDocuments());
    }
  };

  const navigateToPath = (nextPath) => {
    if (isSamePath(currentPath, nextPath)) return;
    setBackHistory((prev) => [...prev, currentPath]);
    setForwardHistory([]);
    applyNavigationState(nextPath);
  };

  const goBack = () => {
    if (backHistory.length === 0) return;
    const previousPath = backHistory[backHistory.length - 1];
    setBackHistory((prev) => prev.slice(0, -1));
    setForwardHistory((prev) => [...prev, currentPath]);
    applyNavigationState(previousPath);
  };

  const goForward = () => {
    if (forwardHistory.length === 0) return;
    const nextPath = forwardHistory[forwardHistory.length - 1];
    setForwardHistory((prev) => prev.slice(0, -1));
    setBackHistory((prev) => [...prev, currentPath]);
    applyNavigationState(nextPath);
  };

  const openAddDrawer = () => {
    setIsAdd(true);
    setDrawerLevel(currentLevel);
    setFormData({ ...emptyForm });
    setUploadedFileUrl('');
    setIsDrawerOpen(true);
  };

  const openEditDrawer = (item) => {
    setIsAdd(false);
    setDrawerLevel(currentLevel);
    setFormData({
      id: item.id,
      name: item.name || '',
      description: item.description || '',
      isPublished: isDocumentPublished(item),
    });
    setUploadedFileUrl(item.fileUrl || '');
    setIsDrawerOpen(true);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Drill-down
  // ─────────────────────────────────────────────────────────────────────────

  const resetToRoot = () => navigateToPath({ department: null, category: null, subcategory: null });

  const goToDepartment = () => navigateToPath({ department: selectedDepartment, category: null, subcategory: null });

  const goToCategory = () => navigateToPath({ department: selectedDepartment, category: selectedCategory, subcategory: null });

  const handleOpen = (item) => {
    if (currentLevel === LEVEL.DEPARTMENT) {
      navigateToPath({ department: item, category: null, subcategory: null });
    } else if (currentLevel === LEVEL.CATEGORY) {
      navigateToPath({ department: selectedDepartment, category: item, subcategory: null });
    } else if (currentLevel === LEVEL.SUBCATEGORY) {
      navigateToPath({ department: selectedDepartment, category: selectedCategory, subcategory: item });
    } else {
      // Open document URL in new tab
      if (item.fileUrl) window.open(item.fileUrl, '_blank');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Submit
  // ─────────────────────────────────────────────────────────────────────────

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (drawerLevel === LEVEL.DOCUMENT && !uploadedFileUrl) {
      toast.error('Please upload a document file');
      return;
    }

    const payload = {
      ...formData,
      ...(drawerLevel === LEVEL.CATEGORY && { departmentId: selectedDepartment?.id }),
      ...(drawerLevel === LEVEL.SUBCATEGORY && { categoryId: selectedCategory?.id }),
      ...(drawerLevel === LEVEL.DOCUMENT && { subcategoryId: selectedSubcategory?.id, fileUrl: uploadedFileUrl }),
    };

    if (drawerLevel === LEVEL.DEPARTMENT) dispatch(isAdd ? addSopDepartment(payload) : updateSopDepartment(payload));
    else if (drawerLevel === LEVEL.CATEGORY) dispatch(isAdd ? addSopCategory(payload) : updateSopCategory(payload));
    else if (drawerLevel === LEVEL.SUBCATEGORY) dispatch(isAdd ? addSopSubcategory(payload) : updateSopSubcategory(payload));
    else dispatch(isAdd ? addSopDocument(payload) : updateSopDocument(payload));
  };

  const handleDelete = (item) => {
    const confirmed = window.confirm(`Delete this ${currentLevelCfg.label.toLowerCase()}? This action cannot be undone.`);
    if (!confirmed) return;

    if (currentLevel === LEVEL.DEPARTMENT) dispatch(deleteSopDepartment({ id: item.id }));
    else if (currentLevel === LEVEL.CATEGORY) dispatch(deleteSopCategory({ id: item.id }));
    else if (currentLevel === LEVEL.SUBCATEGORY) dispatch(deleteSopSubcategory({ id: item.id }));
    else dispatch(deleteSopDocument({ id: item.id }));
  };

  const handleTogglePublish = (item) => {
    dispatch(updateSopDocument({ ...item, isPublished: !isDocumentPublished(item) }));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // File upload for Documents
  // ─────────────────────────────────────────────────────────────────────────

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    fileUpload(
      file,
      'SOPDocuments',
      (data) => {
        setUploadedFileUrl(data?.url || '');
        setIsUploading(false);
      },
      (err) => {
        toast.error(`Upload failed: ${err}`);
        setIsUploading(false);
      }
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Document-level DataGrid columns
  // ─────────────────────────────────────────────────────────────────────────

  const documentColumns = [
    { accessorKey: 'id', header: '#', size: 60 },
    { accessorKey: 'name', header: 'Document Name' },
    { accessorKey: 'description', header: 'Description' },
    {
      accessorKey: 'isPublished',
      header: 'Visibility',
      Cell: ({ cell }) => (isDocumentPublished({ isPublished: cell.getValue() }) ? <Label color='success'>Published</Label> : <Label color='warning'>Draft</Label>),
    },
    {
      accessorKey: 'fileUrl',
      header: 'File',
      Cell: ({ cell }) =>
        cell.getValue() ? (
          <Button
            size='small'
            variant='outlined'
            startIcon={<Iconify icon='eva:external-link-outline' />}
            component='a'
            href={cell.getValue()}
            target='_blank'
            rel='noreferrer'
            onClick={(event) => event.stopPropagation()}
          >
            Open
          </Button>
        ) : (
          <Typography variant='caption' color='text.secondary'>
            —
          </Typography>
        ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  // Render helpers
  // ─────────────────────────────────────────────────────────────────────────

  const renderFolderGrid = () => (
    <Grid container spacing={2}>
      {filteredItems.map((item) => (
        <Grid key={item.id} item xs={12} sm={6} md={4} lg={3}>
          <FolderCard item={item} level={currentLevel} onOpen={handleOpen} onEdit={openEditDrawer} isAdmin={isAdmin} />
        </Grid>
      ))}
      {!isLoading && filteredItems.length === 0 && (
        <Grid item xs={12}>
          <Box
            sx={{
              py: 10,
              textAlign: 'center',
              border: '1.5px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              color: 'text.disabled',
            }}
          >
            <Iconify icon={currentLevelCfg.icon} sx={{ width: 48, height: 48, mb: 1 }} />
            <Typography variant='body2'>{searchValue ? `No ${currentLevelCfg.pluralLabel.toLowerCase()} match your search` : `No ${currentLevelCfg.pluralLabel.toLowerCase()} found`}</Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const renderDocumentList = () => (
    <DataGridTable
      name={'SOP Documents'}
      data={filteredItems}
      column={documentColumns}
      isLoading={isLoading}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      enableRowActions={isAdmin}
      renderRowActionItems={(row, closeMenu) =>
        isAdmin && (
          <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
            <Tooltip title={row?.original?.isPublished ? 'Unpublish' : 'Publish'}>
              <IconButton
                color={isDocumentPublished(row?.original) ? 'warning' : 'success'}
                onClick={(event) => {
                  event.stopPropagation();
                  handleTogglePublish(row?.original);
                  closeMenu?.();
                }}
              >
                <Iconify icon={isDocumentPublished(row?.original) ? 'eva:eye-off-outline' : 'eva:eye-outline'} />
              </IconButton>
            </Tooltip>
            <Tooltip title='Edit'>
              <IconButton
                color='info'
                onClick={(event) => {
                  event.stopPropagation();
                  openEditDrawer(row?.original);
                  closeMenu?.();
                }}
              >
                <Iconify icon='eva:edit-2-outline' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Delete'>
              <IconButton
                color='error'
                onClick={(event) => {
                  event.stopPropagation();
                  handleDelete(row?.original);
                  closeMenu?.();
                }}
              >
                <Iconify icon='eva:trash-2-outline' />
              </IconButton>
            </Tooltip>
          </Box>
        )
      }
      onRowClick={(row) => {
        if (row?.original?.fileUrl) window.open(row?.original?.fileUrl, '_blank');
      }}
      isRowClickable={true}
    />
  );

  const pathSegments = [
    {
      key: 'root',
      label: 'SOP',
      isActive: currentLevel === LEVEL.DEPARTMENT,
      onClick: resetToRoot,
    },
    selectedDepartment && {
      key: `department-${selectedDepartment.id}`,
      label: selectedDepartment.name,
      isActive: currentLevel === LEVEL.CATEGORY,
      onClick: goToDepartment,
    },
    selectedCategory && {
      key: `category-${selectedCategory.id}`,
      label: selectedCategory.name,
      isActive: currentLevel === LEVEL.SUBCATEGORY,
      onClick: goToCategory,
    },
    selectedSubcategory && {
      key: `subcategory-${selectedSubcategory.id}`,
      label: selectedSubcategory.name,
      isActive: currentLevel === LEVEL.DOCUMENT,
      onClick: () => {},
    },
  ].filter(Boolean);

  // ─────────────────────────────────────────────────────────────────────────
  // Drawer form content
  // ─────────────────────────────────────────────────────────────────────────

  const renderDrawerForm = () => (
    <>
      <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant='h5'>
          {isAdd ? 'Add' : 'Edit'} {LEVEL_CONFIG[drawerLevel].label}
        </Typography>
      </Box>
      <Grid container spacing={2} sx={{ p: 3 }}>
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <Card sx={{ p: 3 }}>
              <Stack spacing={2}>
                <TextField fullWidth label='Name' value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                <TextField fullWidth multiline rows={3} label='Description' value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </Stack>
            </Card>

            {drawerLevel === LEVEL.DOCUMENT && (
              <Card sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Typography variant='subtitle2'>Upload Document File</Typography>
                  <Button variant='outlined' component='label' startIcon={<Iconify icon='eva:upload-outline' />} disabled={isUploading}>
                    {isUploading ? 'Uploading...' : 'Choose File'}
                    <input hidden type='file' onChange={handleFileChange} />
                  </Button>
                  {uploadedFileUrl && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Iconify icon='eva:checkmark-circle-2-outline' sx={{ color: 'success.main' }} />
                      <Typography variant='caption' noWrap sx={{ flex: 1 }}>
                        {uploadedFileUrl}
                      </Typography>
                      <Button size='small' component='a' href={uploadedFileUrl} target='_blank' rel='noreferrer'>
                        Preview
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Card>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            {drawerLevel === LEVEL.DOCUMENT && (
              <Card sx={{ p: 3 }}>
                <Stack direction='row' alignItems='center' justifyContent='space-between'>
                  <Box>
                    <Typography variant='subtitle2'>Published</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Viewers can see published documents
                    </Typography>
                  </Box>
                  <Switch checked={formData.isPublished} onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })} color='success' />
                </Stack>
              </Card>
            )}
            <Card sx={{ p: 3 }}>
              <Stack spacing={2}>
                <LoadingButton loading={isLoading} variant='contained' size='large' fullWidth onClick={handleSubmit}>
                  {isAdd ? `Add ${LEVEL_CONFIG[drawerLevel].label}` : 'Save Changes'}
                </LoadingButton>
                <Button color='inherit' variant='outlined' size='large' fullWidth onClick={() => setIsDrawerOpen(false)}>
                  Cancel
                </Button>
                {!isAdd && (
                  <Button
                    color='error'
                    variant='outlined'
                    size='large'
                    fullWidth
                    startIcon={<Iconify icon='eva:trash-2-outline' />}
                    onClick={() => {
                      handleDelete(formData);
                      setIsDrawerOpen(false);
                    }}
                  >
                    Delete
                  </Button>
                )}
              </Stack>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Page render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Page title='Standard Operating Procedures'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={currentPathName}
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'SOP' }]}
          action={
            isAdmin && (
              <Button variant='contained' startIcon={<Iconify icon='eva:plus-fill' />} onClick={openAddDrawer}>
                Add {currentLevelCfg.label}
              </Button>
            )
          }
        />

        <Card
          sx={{
            p: 2,
            mb: 3,
            border: `1px solid ${alpha(currentLevelCfg.color, 0.14)}`,
            backgroundColor: alpha(currentLevelCfg.bgColor, 0.55),
          }}
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Stack direction='row' spacing={0.75} alignItems='center'>
                <Tooltip title='Home'>
                  <span>
                    <IconButton
                      onClick={resetToRoot}
                      disabled={!selectedDepartment}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Iconify icon='eva:home-outline' width={20} height={20} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title='Back'>
                  <span>
                    <IconButton
                      onClick={goBack}
                      disabled={backHistory.length === 0}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Iconify icon='eva:arrow-ios-back-fill' width={18} height={18} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title='Forward'>
                  <span>
                    <IconButton
                      onClick={goForward}
                      disabled={forwardHistory.length === 0}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Iconify icon='eva:arrow-ios-forward-fill' width={18} height={18} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                {pathSegments.map((segment, index) => (
                  <Fragment key={segment.key}>
                    {index > 0 && <Iconify icon='eva:arrow-ios-forward-fill' width={12} height={12} sx={{ color: 'text.disabled' }} />}
                    <Chip
                      label={segment.label}
                      clickable={!segment.isActive}
                      onClick={segment.isActive ? undefined : segment.onClick}
                      color={segment.isActive ? 'primary' : 'default'}
                      variant={segment.isActive ? 'filled' : 'outlined'}
                      icon={index === 0 ? <Iconify icon='mdi:file-tree-outline' /> : undefined}
                      sx={{
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                  </Fragment>
                ))}
              </Box>

              <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Chip
                  size='small'
                  icon={<Iconify icon={currentLevelCfg.icon} />}
                  label={currentLevelCfg.pluralLabel}
                  sx={{ bgcolor: alpha(currentLevelCfg.color, 0.14), color: currentLevelCfg.color }}
                />
                <Chip size='small' variant='outlined' label={`${filteredItems.length} items`} />
                {currentLevel === LEVEL.DOCUMENT && isAdmin && <Chip size='small' variant='outlined' label={`${publishedDocumentCount} published / ${draftDocumentCount} drafts`} />}
              </Stack>
            </Stack>

            <Divider />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                fullWidth
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder={`Search ${currentLevelCfg.pluralLabel.toLowerCase()} by name or description`}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Iconify icon='eva:search-fill' width={20} height={20} />
                    </InputAdornment>
                  ),
                }}
              />
              <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Chip
                  size='small'
                  label='All'
                  color={currentLevel !== LEVEL.DOCUMENT || documentVisibility === 'all' ? 'primary' : 'default'}
                  variant={currentLevel !== LEVEL.DOCUMENT || documentVisibility === 'all' ? 'filled' : 'outlined'}
                  onClick={() => setDocumentVisibility('all')}
                />
                {currentLevel === LEVEL.DOCUMENT && isAdmin && (
                  <>
                    <Chip
                      size='small'
                      label='Published'
                      color={documentVisibility === 'published' ? 'success' : 'default'}
                      variant={documentVisibility === 'published' ? 'filled' : 'outlined'}
                      onClick={() => setDocumentVisibility('published')}
                    />
                    <Chip
                      size='small'
                      label='Drafts'
                      color={documentVisibility === 'draft' ? 'warning' : 'default'}
                      variant={documentVisibility === 'draft' ? 'filled' : 'outlined'}
                      onClick={() => setDocumentVisibility('draft')}
                    />
                  </>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Card>

        {isLoading && currentLevel !== LEVEL.DOCUMENT ? <FolderGridSkeleton /> : currentLevel === LEVEL.DOCUMENT ? renderDocumentList() : renderFolderGrid()}

        {/* Add / Edit Drawer */}
        <SwipeableDrawer
          anchor='top'
          open={isDrawerOpen}
          onOpen={() => setIsDrawerOpen(true)}
          onClose={() => setIsDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: '90%',
              height: '90%',
              marginLeft: '5%',
              marginTop: '3%',
              borderRadius: '10px',
              overflow: 'auto',
            },
          }}
        >
          {renderDrawerForm()}
        </SwipeableDrawer>
      </Container>
    </Page>
  );
}
